/**
 * Detail dialog for one Leena-2 "Top impacts" leaf. Opens when a row in the
 * Top-impacts list is clicked.
 *
 * Two-column layout on desktop:
 *   - Left: the products that have this impact, each in its own box — product
 *     title on top, and a stacked bar in a second row whose length encodes the
 *     product's leaf CPD (relative to the strongest product) and whose internal
 *     segments split that CPD by value chain (with a shared legend above).
 *     Clicking a box selects that product.
 *   - Right: for the selected product, the product-impact reasoning the chat
 *     tool produces — a one-sentence explanation, the causal pathway diagram,
 *     and supporting scientific sources.
 *
 * The columns collapse to a single stacked column on narrow viewports.
 *
 * Team-only (the whole Top-impacts surface is gated upstream). The reasoning is
 * fetched per selected product and is LLM-generated + cached server-side, so
 * the panel shows a loading state on first view of a (product, impact) pair.
 */
import { FinalScoreValence } from '@upright/common/model/output/scores/drilldowns/types';
import {
  Leena2ImpactProduct,
  Leena2TopImpact,
} from '@upright/common/model/output/scores/drilldowns/leena2-top-impacts-types';
import { Valence, ValueChain } from '@upright/common/model/types';
import { Modal } from '@upright/ui/common/generic/components/modal/Modal';
import { PathwayDiagram } from '@upright/ui/common/upright/impact-reasoning/pathway-diagram';
import { ScientificSources } from '@upright/ui/common/upright/impact-reasoning/scientific-sources';
import {
  getValueChainFill,
  valueChainLabel,
  valueChainSegments,
  ValueChainSegment,
} from '@upright/ui/common/upright/model/output/scores/net-impact-chart/value-chain-fill';
import { toLeena2Interval } from '@upright/ui/common/upright/model/output/scores/leena2-uncertainty/format';
import { Leena2IntervalText } from '@upright/ui/common/upright/model/output/scores/leena2-uncertainty/interval-text';
import { useShowLeena2Uncertainty } from '@upright/ui/common/upright/model/output/scores/leena2-uncertainty/use-show-leena2-uncertainty';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { ResearchPreviewBadge } from '../leena2-research-preview-badge';
import { useProductImpactReasoning } from './use-product-impact-reasoning';

/** Stable left-to-right order for value-chain segments and the legend, matching
 *  the main impact chart (UPSTREAM → INTERNAL → DOWNSTREAM, ANY last). */
const VALUE_CHAIN_ORDER: ValueChain[] = [
  ValueChain.UPSTREAM,
  ValueChain.INTERNAL,
  ValueChain.DOWNSTREAM,
  ValueChain.ANY,
];

/** First letter uppercased, mirroring the list rows (descriptions are stored lowercase). */
function sentenceCase(text: string): string {
  return text.length > 0 ? text[0].toUpperCase() + text.slice(1) : text;
}

/** Leaf CPD shown next to each product, in cents-per-dollar (value-add corrected). */
function formatCpd(cdp: number): string {
  return `${cdp.toFixed(2)} ¢/$`;
}

export function Leena2ImpactDetailsDialog({
  impact,
  products,
  valence,
  onClose,
}: {
  /** The clicked leaf impact, or null when the dialog is closed. */
  impact: Leena2TopImpact | null;
  /** Products contributing this leaf, ranked desc by weighted value. */
  products: Leena2ImpactProduct[];
  /** Category valence — colors each product's bar when there is no genuine
   *  value-chain split to show (legacy / ANY-only data). */
  valence: FinalScoreValence;
  onClose: () => void;
}) {
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  // Clef results with "Show Leena-2 uncertainty" on: each product's CPD
  // interval after its CPD (generative results have none).
  const showUncertainty = useShowLeena2Uncertainty();

  // Default-select the highest-contribution product whenever the impact changes
  // (keyed on the impact only, so re-renders don't clobber the user's pick).
  const impactId = impact?.impactId;
  useEffect(() => {
    setSelectedCode(products.at(0)?.code ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impactId]);

  // `.at(0)` (not `[0]`) so the type is correctly `… | undefined` when the
  // product list is empty — the guards below depend on that.
  const selected = products.find(p => p.code === selectedCode) ?? products.at(0);

  // PLT-5685: two-stage load. The reasoning text + pathway come from one fast
  // LLM call; the scientific-article search is a slow web-search pipeline that
  // used to block the whole right column. So fetch reasoning + pathway first
  // (`includeArticles: false`) and render them immediately, then fetch the
  // articles in a second call gated on the first having succeeded — by then the
  // reasoning is cached server-side, so the second call only pays the article
  // cost and never regenerates the reasoning.
  const reasoningEnabled = impact !== null && selected !== undefined;
  const reasoningQuery = useProductImpactReasoning({
    productCode: selected?.code,
    productDescription: selected?.name,
    impactDescription: impact?.description,
    includeArticles: false,
    enabled: reasoningEnabled,
  });
  const reasoning = reasoningQuery.data;

  const articlesQuery = useProductImpactReasoning({
    productCode: selected?.code,
    productDescription: selected?.name,
    impactDescription: impact?.description,
    includeArticles: true,
    enabled: reasoningEnabled && reasoningQuery.isSuccess,
  });

  // Per-product value-chain segments, sourced ONLY from `product.valueChain` —
  // the leena2 impact-topic × product-pair split (backend builds it from the
  // `impact_metric` vc columns, written from leena2's own per-leaf value-chain
  // snapshot). We deliberately do NOT fall back to the regular model's
  // impactScoresByIVV splits, nor to an across-products aggregate — only this
  // per-pair leena2 split is shown. Always rendered when available (no chart
  // value-chain toggle gating); legacy / ANY-only data with no genuine split
  // falls back to a single valence bar. Bars are length-scaled to the strongest
  // product's CPD so they read as a visual ranking of leaf CPD across products.
  const segmentsByCode = new Map<string, ValueChainSegment[]>();
  const presentValueChains = new Set<ValueChain>();
  for (const product of products) {
    const segments = valueChainSegments(product.valueChain);
    segmentsByCode.set(product.code, segments);
    for (const segment of segments) {
      presentValueChains.add(segment.valueChain);
    }
  }
  const legendValueChains = VALUE_CHAIN_ORDER.filter(vc => presentValueChains.has(vc));
  const maxCdp = products.reduce((max, p) => Math.max(max, p.cdp), 0);

  return (
    <Modal isOpen={impact !== null} onClose={onClose} maxInlineSize="64rem" aria-label="Impact detail">
      {impact && (
        <Body>
          <Header>
            <EyebrowRow>
              <Eyebrow>Impact</Eyebrow>
              <ResearchPreviewBadge>Beta preview</ResearchPreviewBadge>
            </EyebrowRow>
            <Title>{sentenceCase(impact.description)}</Title>
          </Header>

          <Columns>
            <LeftColumn>
              <SectionTitle>Products with this impact ({products.length})</SectionTitle>
              {products.length === 0 ? (
                <Muted>No products with this impact.</Muted>
              ) : (
                <ProductBoxes>
                  {products.map(product => {
                    const segments = segmentsByCode.get(product.code) ?? [];
                    const barPct = maxCdp > 0 ? (product.cdp / maxCdp) * 100 : 0;
                    return (
                      <ProductBox
                        key={product.code}
                        type="button"
                        $active={product.code === selected?.code}
                        onClick={() => {
                          setSelectedCode(product.code);
                        }}
                        title={`${product.code} — ${product.name}`}
                      >
                        <ProductBoxHeader>
                          <ProductName>{product.name}</ProductName>
                          <ProductCpd>
                            {formatCpd(product.cdp)}
                            {showUncertainty && (
                              <ProductCpdInterval
                                interval={toLeena2Interval(product.cdpP10, product.cdpP90)}
                                format={v => v.toFixed(2)}
                              />
                            )}
                          </ProductCpd>
                        </ProductBoxHeader>
                        <ProductBarTrack>
                          <ProductBar style={{ width: `${barPct}%` }}>
                            {segments.length > 0 ? (
                              segments.map(segment => (
                                <ProductBarSegment
                                  key={segment.valueChain}
                                  style={{
                                    width: `${segment.fraction * 100}%`,
                                    background: getValueChainFill(segment.valueChain),
                                  }}
                                  title={`${valueChainLabel(segment.valueChain)} ${Math.round(segment.fraction * 100)}%`}
                                />
                              ))
                            ) : (
                              <ProductBarSolo $valence={valence} />
                            )}
                          </ProductBar>
                        </ProductBarTrack>
                      </ProductBox>
                    );
                  })}
                </ProductBoxes>
              )}
              {legendValueChains.length > 0 && (
                <ValueChainLegend>
                  {legendValueChains.map(vc => (
                    <ValueChainLegendItem key={vc}>
                      <ValueChainSwatch style={{ background: getValueChainFill(vc) }} />
                      {valueChainLabel(vc)}
                    </ValueChainLegendItem>
                  ))}
                </ValueChainLegend>
              )}
            </LeftColumn>

            <RightColumn>
              <SectionTitle>
                Why <ProductEmphasis>{selected ? selected.name : 'this product'}</ProductEmphasis> {impact.description}?
              </SectionTitle>
              {reasoningQuery.isLoading && <ReasoningSkeleton />}
              {reasoningQuery.error && <Muted>Couldn’t load reasoning for this product.</Muted>}
              {reasoning && (
                <>
                  {reasoning.reasoning && <Reasoning>{reasoning.reasoning}</Reasoning>}
                  {reasoning.pathwayData && (
                    <PathwayWrap>
                      <PathwayDiagram data={reasoning.pathwayData} />
                    </PathwayWrap>
                  )}
                  <SourcesBlock>
                    <SectionTitle>Related scientific literature</SectionTitle>
                    {/* PLT-5685: articles are the slow second stage — show a
                        placeholder until they land rather than blocking the
                        reasoning + pathway above on the web-search pipeline. */}
                    {articlesQuery.error ? (
                      <Muted>Couldn’t load scientific sources.</Muted>
                    ) : articlesQuery.data ? (
                      <ScientificSources articles={articlesQuery.data.articles} />
                    ) : (
                      <SourcesSkeleton />
                    )}
                  </SourcesBlock>
                </>
              )}
            </RightColumn>
          </Columns>
        </Body>
      )}
    </Modal>
  );
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
  padding: var(--space__lg);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/* Eyebrow + "Beta preview" badge on one row, so the whole reasoning & pathways
   dialog is marked as preview output (matching the Top-impacts list it opens from). */
const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__sm);
`;

const Eyebrow = styled.div`
  font-size: var(--font-size__xs);
  font-weight: var(--font-weight__medium);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color__gray-500);
`;

const Title = styled.h2`
  margin: 0;
  font-size: var(--font-size__lg);
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
`;

/* Two-column body: products on the left, reasoning (explanation + pathway +
   sources) on the right. Collapses to a single stacked column on narrow
   viewports so the dialog stays usable on small screens. */
const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 320px) minmax(0, 1fr);
  gap: var(--space__2xl);
  align-items: start;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: var(--space__lg);
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
  min-width: 0;
`;

/* Light divider between the two columns (becomes a top divider when the
   columns stack on narrow viewports). */
const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
  min-width: 0;
  border-left: 1px solid var(--color__gray-200);
  padding-left: var(--space__xl);
  @media (max-width: 720px) {
    border-left: none;
    padding-left: 0;
    border-top: 1px solid var(--color__gray-200);
    padding-top: var(--space__lg);
  }
`;

const SectionTitle = styled.div`
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  color: var(--color__gray-600);
`;

/* Bolds the product name within the "Why <product> <impact>?" section title. */
const ProductEmphasis = styled.span`
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
`;

/* Shared value-chain legend below the product boxes — only the value chains
   that actually appear in the products' splits are listed. */
const ValueChainLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space__sm);
  font-size: var(--font-size__xs);
  color: var(--color__gray-700);
`;

const ValueChainLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ValueChainSwatch = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
`;

const ProductBoxes = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
`;

/* One product: title on top, a CPD-scaled stacked bar in the second row.
   Clickable — selecting it drives the reasoning shown in the right column. */
const ProductBox = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--space__xs);
  width: 100%;
  padding: var(--space__sm) var(--space__md);
  border-radius: var(--border-radius__md);
  text-align: left;
  cursor: pointer;
  border: 1px solid ${p => (p.$active ? 'var(--color__lilac-800)' : 'var(--color__gray-200)')};
  background: ${p => (p.$active ? 'var(--color__lilac-50)' : 'var(--color__white)')};
  &:hover {
    border-color: var(--color__lilac-800);
  }
`;

const ProductBoxHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space__sm);
`;

const ProductName = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  color: var(--color__gray-900);
`;

const ProductCpd = styled.div`
  flex-shrink: 0;
  font-size: var(--font-size__xs);
  color: var(--color__gray-600);
  font-variant-numeric: tabular-nums;
`;

const ProductCpdInterval = styled(Leena2IntervalText)`
  margin-left: 4px;
`;

/* Full-width track; the colored `ProductBar` inside is length-scaled to the
   product's CPD relative to the strongest product in the list. */
const ProductBarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: var(--color__gray-100);
  border-radius: var(--border-radius__full);
  overflow: hidden;
`;

const ProductBar = styled.div`
  display: flex;
  height: 100%;
  border-radius: inherit;
  overflow: hidden;
`;

const ProductBarSegment = styled.div`
  height: 100%;
`;

/* Single-color fill for products with no genuine value-chain split (legacy /
   ANY-only data) — colored by category valence to match the chart. */
const ProductBarSolo = styled.div<{ $valence: FinalScoreValence }>`
  width: 100%;
  height: 100%;
  background: ${p => (p.$valence === Valence.POSITIVE ? 'var(--color__teal-700)' : 'var(--color__purple-700)')};
`;

const Reasoning = styled.p`
  margin: 0;
  font-size: var(--font-size__md);
  line-height: 1.5;
  color: var(--color__gray-900);
`;

/* Light gray frame around the (internally scrollable) pathway diagram so it
   reads as a distinct unit within the reasoning column. */
const PathwayWrap = styled.div`
  border: 1px solid var(--color__gray-200);
  border-radius: var(--border-radius__md);
  padding: var(--space__sm);
  overflow: hidden;
`;

const SourcesBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
  margin-top: var(--space__lg);
`;

const Muted = styled.div`
  font-size: var(--font-size__sm);
  color: var(--color__gray-500);
`;

/* Loading placeholder shown while the first-stage reasoning is generated /
   fetched (PLT-5685). Mirrors the eventual layout of stage 1: a two-line
   explanation and the framed pathway diagram. The scientific sources are a
   separate second stage with their own placeholder (`SourcesSkeleton`). */
function ReasoningSkeleton() {
  return (
    <SkeletonWrap aria-hidden="true">
      <SkeletonGroup>
        <Skeleton width="100%" height={14} />
        <Skeleton width="85%" height={14} />
      </SkeletonGroup>
      <Skeleton width="100%" height={180} borderRadius={8} />
    </SkeletonWrap>
  );
}

/* Placeholder for the scientific-sources list while the second-stage article
   fetch is still in flight (PLT-5685). The section title is rendered by the
   caller, so this is just the source cards. */
function SourcesSkeleton() {
  return (
    <SkeletonGroup aria-hidden="true">
      <Skeleton width="100%" height={56} borderRadius={6} />
      <Skeleton width="100%" height={56} borderRadius={6} />
    </SkeletonGroup>
  );
}

const SkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__md);
`;

/* Flex column so each skeleton line stacks regardless of the library element's
   own display, with tight intra-group spacing. */
const SkeletonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__xs);
`;
